import { ElementType, ReactNode } from 'react';
import { GestureResponderEvent, Pressable, View } from 'react-native';
import { twMerge } from 'tailwind-merge';
import { Loader } from './Loader';
import { Typography } from './Text';

export type ButtonSize = 'large' | 'medium' | 'small';
export type ButtonColor =
  | 'primary'
  | 'gray'
  | 'white'
  | 'secondary'
  | 'transparent';

const backgroundColors = {
  primary:
    'bg-prim-blue hover:bg-prim-blue-dark active:bg-prim-blue-dark active:opacity-20',
  gray: 'bg-[#646464] hover:bg-prim-blue active:bg-prim-blue',
  white:
    'bg-white hover:bg-neutral-600 active:bg-neutral-600 active:opacity-20',
  secondary:
    'bg-transparent hover:bg-neutral-600 active:bg-neutral-600 active:opacity-20',
  transparent: 'bg-transparent',
  disabled: 'bg-neutral-800',
} as const;

const paddings = {
  large: 'py-3 px-6',
  medium: 'py-2.5 px-4',
  small: 'py-1.5 px-3',
} as const;

export function Button({
  children,
  text,
  size = 'large',
  color = 'primary',
  icon: Icon,
  loading,
  onPress,
  disabled,
  className,
  testID,
  fullWidth = true,
}: {
  children?: ReactNode;
  onPress?: (event: GestureResponderEvent) => void;
  text?: string;
  size?: ButtonSize;
  color?: ButtonColor;
  icon?: ElementType;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  testID?: string;
  fullWidth?: boolean;
}) {
  const backgroundColor = backgroundColors[disabled ? 'disabled' : color];
  // const bgColor = backgroundColors[disabled ? 'disabled' : color];
  const padding = paddings[size];
  const width = fullWidth ? 'w-full' : '';
  const textSize = size == 'large' ? 'normal' : 'small';
  const textStyle = disabled
    ? 'text-[#293D7A]'
    : color == 'white'
    ? 'text-prim-black group-hover:text-white group-active:text-white'
    : 'text-white';
  const iconStyle =
    color == 'white'
      ? 'fill-prim-black group-hover:fill-white group-active:fill-white'
      : 'fill-white';
  const border =
    color == 'secondary' && !disabled
      ? 'border border-white hover:border-0'
      : '';

  return (
    <Pressable
      testID={testID}
      disabled={disabled || loading}
      onPress={(ev) => onPress?.(ev)}
      role="button"
      className={twMerge(
        'flex group text-center flex-row justify-center items-center rounded-[100px] self-start',
        width,
        backgroundColor,
        border,
        padding,
        className
      )}
    >
      {loading ? (
        <Loader color="#FFFFFF" />
      ) : (
        children || (
          <>
            {Icon && (
              <View className="mr-2 h-6 w-6 flex items-center justify-center">
                <Icon className={iconStyle} />
              </View>
            )}
            <Typography
              className={twMerge('text-center', textStyle)}
              variant="callToAction"
              size={textSize}
            >
              {text}
            </Typography>
          </>
        )
      )}
    </Pressable>
  );
}
